import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { applyPhoneMask, applyZipCodeMask, fetchAddressByZipCode } from "../../utils/masks";
import { useNotification } from "../../contexts/NotificationContext";
import { authFetch } from "../../utils/authFetch";

export default function EditarResponsavel() {
    const { id } = useParams();
    const url = `guardian/${id}`;    
    const navigate = useNavigate();
    const location = useLocation();
    const { showNotification } = useNotification();
    
    // Verificar se veio da aba de responsáveis de um paciente
    const patientId = location.state?.patientId;
    const patientGuardianId = location.state?.patientGuardianId; // ID do relacionamento

    const [form, setForm] = useState({
        id: Number(id),
        name: "",
        email: "",
        address: "",
        neighborhood: "",
        addressNumber: "",
        addressComplement: "",
        state: "",
        city: "",
        phone: "",
        cellphone: "",
        zipCode: "",
        userId: null
    });

    const [guardianType, setGuardianType] = useState("");

    useEffect(() => {
        async function fetchData() {
            try {
                const response = await authFetch(url, {                    
                });

                if (!response.ok) throw new Error("Erro ao buscar responsável");

                const data = await response.json();
                setForm(data);

                // Se veio da aba de responsáveis de um paciente, buscar o relacionamento para obter o parentesco
                if (patientId) {
                    try {
                        let relationship = null;
                        
                        // Se tivermos o ID do relacionamento, usar ele diretamente
                        if (patientGuardianId) {
                            const pgResponse = await authFetch(`patient-guardian/${patientGuardianId}`, {
                            });
                            
                            if (pgResponse.ok) {
                                relationship = await pgResponse.json();
                            }
                        }
                        
                        // Se não encontrou pelo ID direto, buscar pelo patientId e guardianId
                        if (!relationship) {
                            const pgResponse = await authFetch(`patient-guardian/patient/${patientId}/guardian/${id}`, {
                            });
                            
                            if (pgResponse.ok) {
                                const pgData = await pgResponse.json();
                                // Se retornar array, pegar o primeiro item
                                relationship = Array.isArray(pgData) ? pgData[0] : pgData;
                            }
                        }
                        
                        // Se encontrou o relacionamento, pegar o guardianType
                        if (relationship && relationship.guardianType) {
                            setGuardianType(relationship.guardianType);
                            console.log("Parentesco carregado:", relationship.guardianType);
                        }
                    } catch (error) {
                        console.error("Erro ao buscar relacionamento patient-guardian:", error);
                    }
                }
            } catch (error) {
                showNotification("danger", "Erro ao buscar responsável");
            }
        }

        fetchData();
    }, [id, patientId, patientGuardianId, showNotification]);

    const handleZipCodeBlur = async () => {
        const data = await fetchAddressByZipCode(form.zipCode);
        if (data) {
            setForm((prevForm) => ({
                ...prevForm,
                address: data.logradouro || "",
                neighborhood: data.bairro || "",
                state: data.uf || "",
                city: data.localidade || "",
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {            
            // Atualizar o responsável
            const response = await authFetch(url, {
                method: "PUT",                
                body: JSON.stringify(form),
            });
            
            if (!response.ok) throw new Error("Erro ao atualizar responsável");

            // Se veio da aba de responsáveis de um paciente, atualizar o relacionamento patient-guardian com o parentesco
            if (patientId && guardianType.trim()) {
                try {
                    let relationshipId = patientGuardianId;
                    
                    // Se não tivermos o ID do relacionamento, buscar
                    if (!relationshipId) {
                        const pgResponse = await authFetch(`patient-guardian/patient/${patientId}/guardian/${id}`, {
                        });
                        
                        if (pgResponse.ok) {
                            const pgData = await pgResponse.json();
                            const relationship = Array.isArray(pgData) ? pgData[0] : pgData;
                            if (relationship && relationship.id) {
                                relationshipId = relationship.id;
                            }
                        }
                    }
                    
                    // Se encontrou o ID do relacionamento, atualizar
                    if (relationshipId) {
                        const updateResponse = await authFetch(`patient-guardian/${relationshipId}`, {
                            method: "PUT",
                            body: JSON.stringify({
                                id: relationshipId,
                                patientId: patientId,
                                guardianId: id,
                                guardianType: guardianType.trim()
                            }),
                        });
                        
                        if (!updateResponse.ok) {
                            console.error("Erro ao atualizar parentesco no relacionamento patient-guardian");
                            showNotification("warning", "Responsável atualizado, mas houve erro ao atualizar o parentesco");
                        }
                    }
                } catch (error) {
                    console.error("Erro ao atualizar relacionamento patient-guardian:", error);
                    showNotification("warning", "Responsável atualizado, mas houve erro ao atualizar o parentesco");
                }
            }

            showNotification("success", "Responsável atualizado com sucesso!");
            
            // Se veio da aba de responsáveis de um paciente, voltar para lá, senão vai para o dashboard
            if (patientId) {
                navigate(`/Pacientes/${patientId}`, { state: { tab: 'responsaveis' } });
            } else {
                navigate("/");
            }
        } catch (error) {
            showNotification("danger", "Erro ao atualizar responsável");
        }
    };

    return (
        <div className="card">
            <div className="card-body p-3">
                <h4 className="mb-4">Editar Responsável</h4>
                <form onSubmit={handleSubmit}>
                    <fieldset className="col-md-6">
                        <div className="mb-3">
                            <label>Nome</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                            />
                        </div>
                        <div className="mb-3">
                            <label>E-mail</label>
                            <input
                                type="email"
                                className="form-control"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                            <small className="text-muted d-flex align-items-center gap-1 mt-1">
                                <i className="bi bi-info-circle text-info"></i>
                                O e-mail será usado para permitir acesso ao aplicativo mobile. Caso não queira permitir acesso, não cadastre um e-mail.
                            </small>
                        </div>
                        <div className="mb-3">
                            <label>Telefone</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: applyPhoneMask(e.target.value) })}
                            />
                        </div>
                        <div className="mb-3">
                            <label>Celular</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.cellphone}
                                onChange={(e) => setForm({ ...form, cellphone: applyPhoneMask(e.target.value) })}
                            />
                        </div>
                        <div className="mb-3">
                            <label>CEP</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.zipCode}
                                onChange={(e) => setForm({ ...form, zipCode: applyZipCodeMask(e.target.value) })}
                                onBlur={handleZipCodeBlur}
                            />
                        </div>
                        <div className="mb-3">
                            <label>Endereço</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                            />
                        </div>
                        <div className="mb-3">
                            <label>Número</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.addressNumber}
                                onChange={(e) => setForm({ ...form, addressNumber: e.target.value })}
                            />
                        </div>
                        <div className="mb-3">
                            <label>Complemento</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.addressComplement}
                                onChange={(e) => setForm({ ...form, addressComplement: e.target.value })}
                            />
                        </div>
                        <div className="mb-3">
                            <label>Bairro</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.neighborhood}
                                onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
                            />
                        </div>
                        <div className="mb-3">
                            <label>Estado</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.state}
                                onChange={(e) => setForm({ ...form, state: e.target.value })}
                            />
                        </div>
                        <div className="mb-3">
                            <label>Cidade</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.city}
                                onChange={(e) => setForm({ ...form, city: e.target.value })}
                            />
                        </div>
                        {patientId && (
                            <div className="mb-3">
                                <label>Parentesco *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={guardianType}
                                    onChange={(e) => setGuardianType(e.target.value)}
                                    placeholder="Ex: Pai, Mãe, Avô, etc."
                                    required
                                />
                            </div>
                        )}
                        <div className="card-footer" style={{ paddingLeft: "0" }}>
                            <button 
                                type="button"
                                onClick={() => {
                                    if (patientId) {
                                        navigate(`/Pacientes/${patientId}`, { state: { tab: 'responsaveis' } });
                                    } else {
                                        navigate('/');
                                    }
                                }}
                                className="btn btn-info btn-sm"
                            >
                                Voltar
                            </button>
                            <button type="submit" className="btn btn-primary btn-sm mx-1">Salvar</button>
                        </div>
                    </fieldset>
                </form>
            </div>
        </div>
    );
}
